import { useState, useEffect, useCallback } from 'react'
import { X, Keyboard } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useT } from '../i18n/index.js'

interface Shortcut {
  keys: string
  description: string
}

/**
 * F29 — Raccourcis clavier globaux.
 * Ctrl+K : focus recherche, Ctrl+N : nouveau contact, Ctrl+M : nouveau mandat, Ctrl+/ : aide
 */
export function KeyboardShortcuts() {
  const t = useT()
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)

  const shortcuts: Shortcut[] = [
    { keys: 'Ctrl + K', description: t.shortcuts?.search || 'Recherche globale' },
    { keys: 'Ctrl + N', description: t.shortcuts?.newContact || 'Nouveau contact' },
    { keys: 'Ctrl + M', description: t.shortcuts?.newMandat || 'Nouveau mandat' },
    { keys: 'Ctrl + /', description: t.shortcuts?.help || 'Afficher les raccourcis' },
  ]

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Ignore if inside input/textarea/contenteditable
      const tag = (e.target as HTMLElement).tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable) return

      const ctrl = e.ctrlKey || e.metaKey

      if (ctrl && e.key === 'k') {
        e.preventDefault()
        // Focus search input if visible
        const searchInput = document.querySelector<HTMLInputElement>('input[type="text"][placeholder]')
        if (searchInput) searchInput.focus()
      } else if (ctrl && e.key === 'n') {
        e.preventDefault()
        navigate('/contacts?new=1')
      } else if (ctrl && e.key === 'm') {
        e.preventDefault()
        navigate('/mandats/new')
      } else if (ctrl && e.key === '/') {
        e.preventDefault()
        setShowHelp((prev) => !prev)
      }
    },
    [navigate],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  if (!showHelp) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={() => setShowHelp(false)}
    >
      <div
        className="bg-[var(--color-surface)] rounded-xl shadow-2xl w-96 max-w-[90vw] border border-[var(--color-border)]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t.shortcuts?.title || 'Raccourcis clavier'}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <Keyboard size={18} className="text-[var(--color-primary)]" />
            <h2 className="text-base font-semibold text-[var(--color-text)]">
              {t.shortcuts?.title || 'Raccourcis clavier'}
            </h2>
          </div>
          <button
            onClick={() => setShowHelp(false)}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-[var(--color-text-secondary)] hover:text-[var(--color-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
            aria-label={t.common.close}
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {shortcuts.map((s) => (
            <div key={s.keys} className="flex items-center justify-between">
              <span className="text-sm text-[var(--color-text)]">{s.description}</span>
              <kbd className="px-2 py-1 rounded bg-[var(--color-background)] border border-[var(--color-border)] text-xs font-mono text-[var(--color-text-secondary)]">
                {s.keys}
              </kbd>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
